import { CloseHealth } from "@common/types/closeHealth";
import {
    DEFAULT_ESTIMATED_TIME_CELL_VARIANT,
    EstimatedTimeCellVariantSchema,
    TaskProgressCellVariantSchema,
} from "@common/types/views/clientsWorkOverviewView";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import {
    ActionsCell,
    CellText,
    CellTooltipTarget,
    type DataGridCellProps,
    EmptyCellValue,
    PersonCell,
} from "@design-system/DataGrid";
import { HealthIndicator } from "@design-system/HealthIndicator/HealthIndicator";
import { parseCloseId } from "@hooks/useOpenWorkRow";
import { formatWorkDate } from "../cells/WorkCloseDateCell";
import { WorkEstimatedTimeSummary } from "../cells/WorkEstimatedTimeCell";
import {
    formatWorkTaskProgress,
    WorkTaskProgressDisplay,
} from "../cells/WorkTaskProgressCell";
import type { ClientsWorkOverviewRow } from "../clientsWorkOverviewViewConfig";
import { WorkRowActionItems } from "../WorkRowActions";
import { useWorkListActions } from "./WorkListActionsContext";
import { WorkRowPresence } from "./WorkRowPresence";
import { getWorkEstimatedTime, UNKNOWN_APPLIED_BY } from "./workListValues";

type WorkListCellProps = DataGridCellProps<ClientsWorkOverviewRow>;

/** After the Service name: a group's summary in the secondary tone, or a work item's presence stack. */
export const WorkNameTrailing = ({ context, meta }: WorkListCellProps) => {
    const row = context.row.original;
    if (row.rowType === "work") {
        return <WorkRowPresence row={row} />;
    }
    if (row.rowType === "group" && row.summary) {
        return (
            <CellText
                text={` · ${row.summary}`}
                overflow={meta.overflow}
                tone="secondary"
            />
        );
    }
    return null;
};

/** Health as its chip; the column's tooltip gives the reason. */
export const WorkHealthChip = ({ context, meta }: WorkListCellProps) => {
    const row = context.row.original;
    if (row.rowType !== "work" || row.health === CloseHealth.NONE) {
        return <EmptyCellValue empty={meta.empty} />;
    }
    return <HealthIndicator health={row.health} data-health={row.health} />;
};

/**
 * Finished tasks in the column's variant, for work with tasks; the circle
 * reads as "3 / 5 (60%)" on hover. The tooltip lives here, not on the
 * column, because only the cell knows the variant.
 */
export const WorkProgressCell = ({
    context,
    meta,
    variant,
}: WorkListCellProps) => {
    const row = context.row.original;
    if (row.rowType !== "work" || row.totalTaskCount === 0) {
        return <EmptyCellValue empty={meta.empty} />;
    }
    const parsed = TaskProgressCellVariantSchema.safeParse(variant);
    const progressVariant = parsed.success ? parsed.data : "count";
    return (
        <CellTooltipTarget
            tooltip={
                progressVariant === "circle"
                    ? formatWorkTaskProgress({ row, variant: progressVariant })
                    : undefined
            }
        >
            <WorkTaskProgressDisplay row={row} variant={progressVariant} />
        </CellTooltipTarget>
    );
};

/**
 * Estimated time in the column's variant, the per-member breakdown in the
 * column's tooltip; a dash for work with no estimates, as ViewTable showed it.
 */
export const WorkEstimatedTimeListCell = ({
    context,
    meta,
    variant,
}: WorkListCellProps) => {
    const row = context.row.original;
    if (row.rowType !== "work") {
        return <EmptyCellValue empty={meta.empty} />;
    }
    if (getWorkEstimatedTime(row) === undefined) {
        return <EmptyCellValue empty="dash" />;
    }
    const parsed = EstimatedTimeCellVariantSchema.safeParse(variant);
    return (
        <WorkEstimatedTimeSummary
            row={row}
            variant={
                parsed.success
                    ? parsed.data
                    : DEFAULT_ESTIMATED_TIME_CELL_VARIANT
            }
        />
    );
};

/** A client's Open (its workspace), or a work's actions menu; group rows have neither. */
export const WorkListActionsCell = ({ context }: WorkListCellProps) => {
    const actions = useWorkListActions();
    const row = context.row.original;
    if (
        !actions ||
        row.rowType === "group" ||
        (row.rowType === "work" &&
            (!parseCloseId(row.closeId) || isBusyClosesDemoId(row.closeId)))
    ) {
        return null;
    }
    if (row.rowType === "workspace") {
        return (
            <ActionsCell>
                <ActionsCell.Button
                    variant="primary"
                    onClick={() => actions.onOpenWorkspace(row.workspaceId)}
                >
                    Open
                </ActionsCell.Button>
            </ActionsCell>
        );
    }
    return (
        <ActionsCell>
            <ActionsCell.Menu>
                <WorkRowActionItems
                    row={row}
                    callbacks={actions.callbacks}
                    onConfirmationRequest={actions.onConfirmationRequest}
                />
            </ActionsCell.Menu>
        </ActionsCell>
    );
};

/** Who applied the work; "Unknown" (not a member) is plain text, with no avatar. */
export const WorkAppliedByListCell = (props: WorkListCellProps) =>
    props.context.getValue() === UNKNOWN_APPLIED_BY ? (
        <CellText
            text={UNKNOWN_APPLIED_BY.name}
            overflow={props.meta.overflow}
        />
    ) : (
        <PersonCell {...props} fallback={undefined} />
    );

/** Work end; the column's tooltip says how overdue late open work is. */
export const WorkDeliveryDateListCell = ({
    context,
    meta,
}: WorkListCellProps) => {
    const row = context.row.original;
    if (row.rowType !== "work" || !row.workDeliveryDate) {
        return <EmptyCellValue empty={meta.empty} />;
    }
    return formatWorkDate(row.workDeliveryDate);
};
